import {
  BeneficialOwnerControlType,
  ShareholdingType,
  useCreateBeneficialOwner,
  useOwnership,
  usePersonsInEntity,
} from '@commenda/api';
import { Country } from '@prisma/client';
import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { useMemo, useState } from 'react';
import {
  CollapsibleModuleCard,
  FormActions,
} from '../../../form-builder/components/new-incorp-layout';
import { friendlyOwnershipMessage } from './applyOwnershipApiError';
import { ControlTypePicker } from './ControlTypePicker';
import { AddPersonBottomsheet, NewPersonDraft } from './AddPersonBottomsheet';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';

interface AddUltimateBeneficialOwnerFormProps {
  corporationId: number;
  onCancel: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

type CandidatePerson = {
  directorId: number;
  firstName: string;
  lastName: string | null;
  dateOfBirth: string;
  nationality: Country;
};

type CandidateState =
  | { kind: 'plain' }
  | { kind: 'stakeholder' }
  | { kind: 'inherited-only' } // BOR has paths but no directControlType, no Stakeholding
  | { kind: 'direct-ubo' }; // BOR has directControlType (with or without Stakeholding / paths)

export const AddUltimateBeneficialOwnerForm = ({
  corporationId,
  onCancel,
  onSuccess,
  onError,
}: AddUltimateBeneficialOwnerFormProps) => {
  const createBorMutation = useCreateBeneficialOwner(corporationId);

  const { data: ownership } = useOwnership(corporationId);
  const { data: entityPersonsResp } = usePersonsInEntity(corporationId);
  const entityPersons = entityPersonsResp?.persons ?? [];

  const [cardOpen, setCardOpen] = useState(true);
  const [bottomsheetOpen, setBottomsheetOpen] = useState(false);
  // Mutually exclusive — at most one is non-null. Submit hands either
  // directorId (existing) or person (atomic create) to createBor.
  const [selectedDirectorId, setSelectedDirectorId] = useState<number | null>(null);
  const [stagedPerson, setStagedPerson] = useState<NewPersonDraft | null>(null);
  const [controlType, setControlType] = useState<BeneficialOwnerControlType | null>(null);
  const [pickerError, setPickerError] = useState<string | undefined>(undefined);
  const [controlTypeError, setControlTypeError] = useState<string | undefined>(undefined);

  // Per-director state derived from the corp's ownership snapshot. The
  // picker shows captions and disables already-direct / pure-inherited
  // rows. Stakeholders (with or without inherited paths) stay selectable
  // — clicking them adds a directControlType to the existing BOR (or
  // creates one).
  const stateByDirectorId = useMemo(() => {
    const stakeholderIds = new Set(
      (ownership?.shareholdings ?? [])
        .filter((s) => s.type === ShareholdingType.INDIVIDUAL && s.directorId !== null)
        .map((s) => s.directorId as number),
    );
    const borByDirectorId = new Map(
      (ownership?.beneficialOwners ?? []).map((b) => [b.directorId, b]),
    );
    const allDirectorIds = new Set<number>([
      ...Array.from(stakeholderIds),
      ...Array.from(borByDirectorId.keys()),
    ]);
    const map = new Map<number, CandidateState>();
    for (const id of Array.from(allDirectorIds)) {
      const bor = borByDirectorId.get(id);
      const hasStakeholding = stakeholderIds.has(id);
      if (!!bor?.directControlType) {
        map.set(id, { kind: 'direct-ubo' });
      } else if (bor && bor.paths.length > 0 && !hasStakeholding) {
        map.set(id, { kind: 'inherited-only' });
      } else if (hasStakeholding) {
        map.set(id, { kind: 'stakeholder' });
      } else {
        map.set(id, { kind: 'plain' });
      }
    }
    return map;
  }, [ownership]);

  const stateOf = (id: number): CandidateState =>
    stateByDirectorId.get(id) ?? { kind: 'plain' };

  const isBlocked = (id: number): boolean => {
    const s = stateOf(id);
    return s.kind === 'direct-ubo' || s.kind === 'inherited-only';
  };

  // Direct-UBO and inherited-only directors are hidden from the picker —
  // they're already represented on the BOR list, picking them is a no-op.
  // Stakeholders stay in the list (they're a valid candidate for becoming
  // an explicit UBO).
  const candidates: CandidatePerson[] = useMemo(
    () =>
      entityPersons
        .filter((p) => !isBlocked(p.id))
        .map((p) => ({
          directorId: p.id,
          firstName: p.firstName,
          lastName: p.lastName || null,
          dateOfBirth: p.dateOfBirth,
          nationality: (p.nationalities ?? [])[0] ?? Country.US,
        })),
    [entityPersons, stateByDirectorId],
  );

  const STAGED_OPTION_VALUE = '__staged__';
  const personOptions = useMemo(() => {
    const existing = candidates.map((c) => ({
      value: String(c.directorId),
      label: `${c.firstName}${c.lastName ? ` ${c.lastName}` : ''}`,
    }));
    if (stagedPerson) {
      return [
        {
          value: STAGED_OPTION_VALUE,
          label: `${stagedPerson.firstName}${stagedPerson.lastName ? ` ${stagedPerson.lastName}` : ''}`,
        },
        ...existing,
      ];
    }
    return existing;
  }, [candidates, stagedPerson]);

  const handlePersonPick = (value: string) => {
    if (!value) {
      setSelectedDirectorId(null);
      setStagedPerson(null);
      return;
    }
    if (value === STAGED_OPTION_VALUE) return;
    const id = Number(value);
    if (isBlocked(id)) return; // Informational only — no action.
    setSelectedDirectorId(id);
    setStagedPerson(null);
    setPickerError(undefined);
  };

  const handleStagedPerson = (draft: NewPersonDraft) => {
    setStagedPerson(draft);
    setSelectedDirectorId(null);
    setPickerError(undefined);
  };

  // Pre-submit validation collects every issue at once; each shows inline
  // and a single toast announces "fix the highlighted fields".
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nextPickerError =
      !selectedDirectorId && !stagedPerson
        ? "Pick a person or click 'Add person' to create a new one."
        : undefined;
    const nextControlTypeError =
      !controlType ? 'Select a control type.' : undefined;
    setPickerError(nextPickerError);
    setControlTypeError(nextControlTypeError);
    if (nextPickerError || nextControlTypeError) {
      onError?.(SUBMIT_VALIDATION_TOAST);
      return;
    }

    if (!!selectedDirectorId && isBlocked(selectedDirectorId)) return;

    try {
      // BE accepts directorId or person; either path lands the BOR + (if
      // staged) the Director in one transaction.
      await createBorMutation.mutateAsync({
        directorId: selectedDirectorId ?? undefined,
        person: stagedPerson ?? undefined,
        kind: 'manual',
        directControlType: controlType!,
      });
      onSuccess?.('Beneficial owner added.');
      onCancel();
    } catch (err) {
      onError?.(friendlyOwnershipMessage(err));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-2xl pb-8xl">
      <CollapsibleModuleCard
        title="Beneficial owner details"
        isExpanded={cardOpen}
        onExpandedChange={setCardOpen}
      >
        <FieldWrapper>
          <Label required>Person</Label>
          <SingleCombobox
            canClearSelection={false}
            options={personOptions}
            value={
              !!selectedDirectorId
                ? String(selectedDirectorId)
                : !!stagedPerson
                  ? STAGED_OPTION_VALUE
                  : ''
            }
            setValue={handlePersonPick}
            placeholder="Select a person"
            searchPlaceholder="Search people…"
            notFoundText="No matching people on this entity."
            addItemConfig={{
              shouldAllowAdd: true,
              onAddItem: () => setBottomsheetOpen(true),
              placeholder: 'Add person',
            }}
            renderOption={({ option, onSelect }) => {
              const isStaged = option.value === STAGED_OPTION_VALUE;
              const id = isStaged ? null : Number(option.value);
              const state = id !== null ? stateOf(id) : { kind: 'plain' as const };
              const caption = isStaged
                ? '(New)'
                : state.kind === 'stakeholder'
                  ? '(Stakeholder)'
                  : null;
              return (
                <button
                  type="button"
                  onClick={() => onSelect(option.value)}
                  className="flex w-full items-center gap-md text-left"
                >
                  <span className="txt-small-medium text-text-primary">
                    {option.label}
                  </span>
                  {caption && (
                    <span className="txt-extra-small-regular text-text-tertiary">
                      {caption}
                    </span>
                  )}
                </button>
              );
            }}
          />
          {pickerError && <InputErrorMessage message={pickerError} />}
        </FieldWrapper>

        <FieldWrapper>
          <Label required>Control type</Label>
          <ControlTypePicker
            value={controlType ?? undefined}
            onChange={(v) => {
              setControlType(v);
              setControlTypeError(undefined);
            }}
          />
          {controlTypeError && (
            <InputErrorMessage message={controlTypeError} />
          )}
        </FieldWrapper>
      </CollapsibleModuleCard>

      <FormActions
        hideBack
        showCancel
        onCancel={onCancel}
        nextLabel="Add UBO"
        isLoading={createBorMutation.isPending}
      />

      <AddPersonBottomsheet
        isOpen={bottomsheetOpen}
        onClose={() => setBottomsheetOpen(false)}
        onStaged={handleStagedPerson}
        onError={onError}
      />
    </form>
  );
};
